// src/services/reportService.js
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

const REPORTS_COLLECTION = 'payback-reports';

export const reportService = {
  /**
   * Create a new report
   */
  async createReport(reportData) {
    try {
      const reportRef = doc(collection(db, REPORTS_COLLECTION));
      const report = {
        ...reportData,
        id: reportRef.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(reportRef, report);
      return { success: true, id: reportRef.id, data: report };
    } catch (error) {
      console.error('Error creating report:', error);
      throw error;
    }
  },

  /**
   * Get report by ID
   */
  async getReport(reportId) {
    try {
      const reportRef = doc(db, REPORTS_COLLECTION, reportId);
      const reportSnap = await getDoc(reportRef);
      
      if (reportSnap.exists()) {
        return { ...reportSnap.data(), id: reportSnap.id };
      }
      return null;
    } catch (error) {
      console.error('Error getting report:', error);
      throw error;
    }
  },

  /**
   * Update report
   */
  async updateReport(reportId, updates) {
    try {
      const reportRef = doc(db, REPORTS_COLLECTION, reportId);
      await updateDoc(reportRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating report:', error);
      throw error;
    }
  },

  /**
   * Get reports by lender ID
   */
  async getReportsByLender(lenderId) {
    try {
      const q = query(
        collection(db, REPORTS_COLLECTION),
        where('lenderId', '==', lenderId),
        orderBy('createdDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
    } catch (error) {
      console.error('Error getting lender reports:', error);
      throw error;
    }
  },

  /**
   * Get reports by borrower mobile
   */
  async getReportsByBorrower(mobile) {
    try {
      const q = query(
        collection(db, REPORTS_COLLECTION),
        where('borrowerMobile', '==', mobile),
        orderBy('createdDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
    } catch (error) {
      console.error('Error getting borrower reports:', error);
      throw error;
    }
  },

  /**
   * Get public reports (status is published)
   */
  async getPublicReports(searchTerm = '', statusFilter = null) {
    try {
      let q;
      
      if (statusFilter) {
        q = query(
          collection(db, REPORTS_COLLECTION),
          where('status', '==', statusFilter),
          orderBy('createdDate', 'desc'),
          limit(100)
        );
      } else {
        q = query(
          collection(db, REPORTS_COLLECTION),
          where('status', 'in', [
            'PUBLISHED_UNRESOLVED',
            'PUBLISHED_DISPUTED',
            'PUBLISHED_SETTLED'
          ]),
          orderBy('createdDate', 'desc'),
          limit(100)
        );
      }
      
      const querySnapshot = await getDocs(q);
      let reports = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
      
      // Client-side filtering for search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        reports = reports.filter(r => 
          r.borrowerName?.toLowerCase().includes(term) ||
          r.borrowerMobile?.includes(searchTerm) ||
          r.id?.includes(searchTerm)
        );
      }
      
      return reports;
    } catch (error) {
      console.error('Error getting public reports:', error);
      throw error;
    }
  },

  /**
   * Search reports by mobile number (exact match)
   */
  async searchReportsByMobile(mobile) {
    try {
      const q = query(
        collection(db, REPORTS_COLLECTION),
        where('borrowerMobile', '==', mobile),
        where('status', 'in', [
          'PUBLISHED_UNRESOLVED',
          'PUBLISHED_DISPUTED',
          'PUBLISHED_SETTLED'
        ])
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));
    } catch (error) {
      console.error('Error searching reports:', error);
      throw error;
    }
  },

  /**
   * Update report status
   */
  async updateReportStatus(reportId, status) {
    try {
      const reportRef = doc(db, REPORTS_COLLECTION, reportId);
      await updateDoc(reportRef, {
        status,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  },

  /**
   * Add borrower response
   */
  async addBorrowerResponse(reportId, response) {
    try {
      const reportRef = doc(db, REPORTS_COLLECTION, reportId);
      await updateDoc(reportRef, {
        borrowerResponse: response.text,
        borrowerResponseType: response.type,
        borrowerResponseDate: new Date().toLocaleDateString(),
        borrowerEvidenceAttachments: response.attachments || [],
        status: 'PUBLISHED_DISPUTED',
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error adding borrower response:', error);
      throw error;
    }
  },

  /**
   * Get daily post count for a user
   */
  async getDailyPostCount(userId) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayTimestamp = Timestamp.fromDate(today);
      
      const q = query(
        collection(db, REPORTS_COLLECTION),
        where('lenderId', '==', userId),
        where('createdAt', '>=', todayTimestamp)
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting daily post count:', error);
      throw error;
    }
  },

  /**
   * Get total report count for a user
   */
  async getTotalReportCount(userId) {
    try {
      const q = query(
        collection(db, REPORTS_COLLECTION),
        where('lenderId', '==', userId)
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting total report count:', error);
      throw error;
    }
  }
};